"use client";

import { Badge, BadgeProps, styled, useMediaQuery } from "@mui/material";
import { usePathname } from "next/navigation";
import routes from "@/app.route";
import config from "@/app.config";
import useDrawer from "@/stores/drawer";
import useCart from "@/stores/cart";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import LocalPhoneOutlinedIcon from "@mui/icons-material/LocalPhoneOutlined";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";

const StyledBadge = styled(Badge)<BadgeProps>(({ theme }) => ({
  "& .MuiBadge-badge": {
    right: "15px",
    top: "5px",
    padding: "0px",
    minWidth: "16px",
    width: "16px",
    height: "16px",
  },
}));

export default function BottomBar() {
  const matches = useMediaQuery("(min-width:1024px)");
  const pathname = usePathname();
  const { toggleDrawerState } = useDrawer();
  const { items } = useCart();

  if (
    pathname.startsWith(routes.product.url.replace(":slug", "")) ||
    pathname == routes.login.url
  ) {
    return null;
  }

  if (matches) {
    return null;
  }

  const navigations = [
    {
      label: "Home",
      icon: <HomeOutlinedIcon color="inherit" fontSize="inherit" />,
      url: routes.home.url,
      active: pathname == routes.home.url,
    },
    {
      label: "Menu",
      icon: <MenuRoundedIcon color="inherit" fontSize="inherit" />,
      onClick: toggleDrawerState,
      active: false,
    },
    {
      label: "Cart",
      icon: <ShoppingCartOutlinedIcon color="inherit" fontSize="inherit" />,
      url: routes.cart.url,
      active: pathname == routes.cart.url,
      badge: items.length,
    },
    {
      label: "WhatsApp",
      icon: <WhatsAppIcon color="inherit" fontSize="inherit" />,
      url: `https://wa.me/+91${config.contact.whatsappNumber}`,
      active: false,
      external: true,
    },
    {
      label: "Call",
      icon: <LocalPhoneOutlinedIcon color="inherit" fontSize="inherit" />,
      url: `tel:+91${config.contact.primaryPhone}`,
      active: false,
      external: true,
    },
  ];

  return (
    <div className="sticky bottom-0 left-0 right-0 py-4 px-6 z-10">
      <div className="max-w-md mx-auto">
        <nav className="relative bg-black/40 backdrop-blur-2xl rounded-3xl px-4 py-2 border border-white/20 shadow-2xl">
          <div className="absolute inset-0 bg-linear-to-t from-white/5 to-transparent rounded-3xl pointer-events-none"></div>

          <div className="relative flex items-center justify-around">
            {navigations.map(
              (
                { label, icon, active, onClick, url, badge, external },
                index,
              ) => {
                const Icon = () => (
                  <div
                    className={`text-white relative rounded-xl transition-all duration-300 [&_svg]:size-5! size-8 grid place-items-center ${
                      active
                        ? "bg-white/20"
                        : "group-hover:bg-white/10 [&_svg]:size-6!"
                    }`}
                  >
                    {icon}
                  </div>
                );

                const content = (
                  <>
                    {badge ? (
                      <StyledBadge badgeContent={badge} color="primary">
                        <Icon />
                      </StyledBadge>
                    ) : (
                      <Icon />
                    )}
                    <span className="text-white text-[0.65rem] font-medium transition-all duration-200">
                      {label}
                    </span>
                  </>
                );

                if (onClick) {
                  return (
                    <button
                      key={index}
                      onClick={onClick}
                      aria-label={label}
                      className="flex flex-col items-center transition-all duration-200 group"
                    >
                      {content}
                    </button>
                  );
                }

                return (
                  <a
                    key={index}
                    href={url}
                    aria-label={label}
                    className="flex flex-col items-center transition-all duration-200 group"
                    {...(external
                      ? { target: "_blank", rel: "noopener noreferrer" }
                      : {})}
                  >
                    {content}
                  </a>
                );
              },
            )}
          </div>
        </nav>
      </div>
    </div>
  );
}
